import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { ACTIVE_INPUT_OPACITY } from '@utils/constants';
import type { IconName } from '@components/Icon/BaseIcon';
import { BaseIcon } from '@components/Icon/BaseIcon';

export interface SwitchProps {
  /**
   * The unique ID (attribute) of the html input element.
   */
  id?: string;
  /**
   * Name (attribute) of the html input element.
   */
  name?: string;
  /**
   * Value of the html input element.
   */
  value?: string;
  /**
   * Specifies if the input is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies if the input should be checked by default.
   */
  isChecked: boolean;
  /**
   * Styles overrides.
   */
  className?: string;
  /**
   * Switch change handler. Called when the switch checkbox state changes.
   */
  onChange: (isChecked: boolean) => void;
  /**
   * Switch label content (React nodes and strings allowed). This appears alongside the switch.
   */
  labelContent?: string | ReactNode;
  /**
   * The label that appears below the switch when the switch is on.
   */
  labelOn?: string;
  /**
   * The label that appears below the switch when the switch is off.
   */
  labelOff?: string;
  /**
   * Specify the size of the Switch toggle.
   */
  size?: SwitchSize;
  /**
   * Specifies an icon to be shown when the switch is active.
   * Defaults to the 'selected' icon.
   */
  iconName?: IconName;
  /**
   * Specifies an icon to be shown when the switch is active but also disabled.
   * Falls back to the `iconName` icon.
   */
  disabledIconName?: IconName;
}

type SwitchSize = 's' | 'm';

const SWITCH_TOGGLE_SIZES = {
  s: tokens.spacer.x2pt5,
  m: tokens.spacer.x3pt5,
} as const;

const SWITCH_ICON_SIZES = {
  s: 12,
  m: 16,
} as const;

const SWITCH_SIZES = {
  s: {
    width: tokens.spacer.x5,
    height: tokens.spacer.x3,
  },
  m: {
    width: tokens.spacer.x6,
    height: tokens.spacer.x4,
  },
} as const;

const Label = styled('label', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{ isDisabled: boolean }>`
  display: inline-flex;
  text-transform: none; // temp solution until the old styles from IWA are removed
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
`;

const Input = styled('input', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{ switchSize: SwitchSize }>`
  opacity: 0;
  position: absolute;
  cursor: inherit;
`;

const SwitchContainer = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
`;

const ToggleContainer = styled('span', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{ size: SwitchSize }>`
  width: ${({ size }) => SWITCH_SIZES[size].width};
  height: ${({ size }) => SWITCH_SIZES[size].height};
  border-radius: 16px;
  background: ${tokens.color.neutral_300};
  display: inline-flex;
  padding: 2px;
  color: ${tokens.color.text_light};

  ${Input}:checked + ${SwitchContainer} & {
    color: ${tokens.color.ui_active};
    background: ${tokens.color.ui_active};
  }

  ${Input}:focus + ${SwitchContainer} & {
    ${tokens.focus()};
  }

  ${Input}:disabled + ${SwitchContainer} & {
    background: ${tokens.color.ui_disabled};
  }

  ${Input}:disabled:checked + ${SwitchContainer} & {
    background: ${tokens.color.ui_active};
    opacity: ${ACTIVE_INPUT_OPACITY};
  }
`;

const Toggle = styled('span', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{ size: SwitchSize }>`
  background: ${tokens.color.ui_background};
  width: ${({ size }) => SWITCH_TOGGLE_SIZES[size]};
  height: ${({ size }) => SWITCH_TOGGLE_SIZES[size]};
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  ${tokens.transition('transform', 120)};

  svg {
    ${tokens.transition('opacity', 120)};
    opacity: 0;
  }

  ${Input}:checked + ${SwitchContainer} ${ToggleContainer} & {
    transform: translateX(16px);

    svg {
      opacity: 1;
    }
  }

  ${Input}:disabled + ${SwitchContainer} ${ToggleContainer} & {
    svg path {
      fill: ${tokens.color.ui_disabled};
    }
  }

  ${Input}:disabled:checked + ${SwitchContainer} ${ToggleContainer} & {
    svg path {
      fill: ${tokens.color.ui_active};
    }
  }
`;

const SwitchLabel = styled.span`
  ${tokens.text('caption')};
  margin-top: ${tokens.spacer.x0};
  display: flex;
  flex-direction: column;
  text-align: center;
  user-select: none;

  ${Input}:checked + ${SwitchContainer} & {
    color: ${tokens.color.ui_active};
  }

  ${Input}:disabled + ${SwitchContainer} & {
    color: ${tokens.color.text_disabled};
  }

  ${Input}:disabled:checked + ${SwitchContainer} & {
    color: ${tokens.color.ui_active};
    opacity: ${ACTIVE_INPUT_OPACITY};
  }
`;

const SwitchLabelText = styled('span', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{ isVisible: boolean }>`
  ${({ isVisible }) => (!isVisible ? 'visibility: hidden;' : '')}

  &:not(:first-of-type) {
    margin-bottom: -1em;
    transform: translateY(-100%);
  }
`;

const LabelContent = styled('span', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{ size: SwitchSize }>`
  display: inline-flex;
  flex: 1 1 auto;
  align-items: center;

  user-select: none;
  margin-left: ${tokens.spacer.x1};
  height: ${({ size }) => SWITCH_SIZES[size].height};

  ${tokens.text('body2')}

  ${Input}:disabled + ${SwitchContainer} + & {
    color: ${tokens.color.text_disabled};
  }
`;

/**
 * A switch (also known as 'toggle') lets users turn something on or off immediately.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/272f9b-switch/b/49c561 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-switch--switch-story Code documentation}
 *
 * @example
 * <Switch labelContent="Switch label" value={} onChange={} />
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      className,
      labelContent,
      isDisabled = false,
      isChecked = false,
      size = 's',
      onChange,
      labelOn,
      labelOff,
      value,
      name,
      id,
      disabledIconName,
      iconName = 'selected',
    }: SwitchProps,
    ref,
  ) => {
    return (
      <Label
        className={className}
        isDisabled={isDisabled}
        role="switch"
        aria-checked={isChecked}
        aria-disabled={isDisabled}
      >
        <Input
          type="checkbox"
          checked={isChecked}
          onChange={() => onChange(!isChecked)}
          disabled={isDisabled}
          id={id}
          ref={ref}
          switchSize={size}
          value={value}
          name={name}
        />
        <SwitchContainer>
          <ToggleContainer size={size}>
            <Toggle size={size}>
              <BaseIcon
                pixelSize={SWITCH_ICON_SIZES[size]}
                icon={(isDisabled && disabledIconName) || iconName}
              />
            </Toggle>
          </ToggleContainer>

          {labelOn || labelOff ? (
            <SwitchLabel>
              <SwitchLabelText isVisible={labelOn && isChecked}>
                {labelOn}
              </SwitchLabelText>

              <SwitchLabelText isVisible={labelOff && !isChecked}>
                {labelOff}
              </SwitchLabelText>
            </SwitchLabel>
          ) : null}
        </SwitchContainer>
        {labelContent ? (
          <LabelContent size={size}>{labelContent}</LabelContent>
        ) : null}
      </Label>
    );
  },
);

Switch.displayName = 'Switch';
